import { useCallback, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty } from 'lodash-es'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react'

import {
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Label,
} from 'Components/_theme'
import { AlertWithError } from 'Components/AlertWithError/AlertWithError'
import { Form } from 'Components/Form/Form'
import { FormOptionalMessage } from 'Components/FormOptionalMessage/FormOptionalMessage'
import { SingleDatePicker } from 'Components/SingleDatePicker/SingleDatePicker'

import { useStore } from 'Stores/index'

import { handleFetchErrorFunctional } from 'Utilities'

import {
  E2E_CANCEL_DEVICE_DATE_PICKER,
  E2E_CANCEL_DEVICE_HEADER,
  E2E_CANCEL_DEVICE_SUBMIT,
} from 'Constants/e2e'
import { SentryErrorType } from 'Constants/errors'
import type {
  mutationCancelDevice,
  mutationCancelDeviceVariables,
} from 'Constants/graphql/mutations/__generated__/mutationCancelDevice'
import type {
  mutationScheduledCancellation,
  mutationScheduledCancellationVariables,
} from 'Constants/graphql/mutations/__generated__/mutationScheduledCancellation'
import { MUTATION_CANCEL_DEVICE } from 'Constants/graphql/mutations/MutationCancelDevice'
import { MUTATION_SCHEDULED_CANCELLATION } from 'Constants/graphql/mutations/MutationScheduledCancellation'

import { iconCancelDevice } from 'Icons/iconCancelDevice'

import {
  CANCEL_DEVICE_DATE_PICKER_ID,
  CANCEL_DEVICE_FORM_FIELDS,
  CANCEL_DEVICE_LEAD_TIME_INFO_ID,
  CANCEL_DEVICE_MODAL_FORM,
  MAX_SCHEDULED_CANCELLATION_MONTHS,
} from './CancelDeviceModal.constants'
import { cancelDeviceModalFormSchema } from './CancelDeviceModal.schema'
import {
  type CancelDeviceModalFormSchema,
  type CancelDeviceModalProps,
  CancellationTypeEnum,
} from './CancelDeviceModal.types'
import { cancelDeviceFormDefaultValues } from './CancelDeviceModal.values'
import { CancellationTypeSelector } from './components/CancellationTypeSelector/CancellationTypeSelector'

export const CancelDeviceModal = observer((props: CancelDeviceModalProps) => {
  const { handleModalSubmit, isScheduledCancellationEnabled, onClose, rental } =
    props
  const { userStore } = useStore()
  const { user } = userStore

  const intl = useIntl()

  const { scheduledCancellationConfig } = rental

  const maxCancellationDate = DateTime.now()
    .plus({ months: MAX_SCHEDULED_CANCELLATION_MONTHS })
    .toISODate()

  const [
    createCancelDeviceCaseItem,
    { loading: isLoadingImmediate, error: errorImmediate },
  ] = useMutation<mutationCancelDevice, mutationCancelDeviceVariables>(
    MUTATION_CANCEL_DEVICE,
    {
      onCompleted: () => {
        handleModalSubmit()
      },
      onError: (error, options) => {
        handleFetchErrorFunctional()(error, {
          location: window?.location || undefined,
          operation: {
            operationName: 'createCancelDeviceCaseItem',
            variables: options?.variables ?? undefined,
          },
          type: SentryErrorType.GQL_ERROR,
          userId: user.userId?.toString() ?? undefined,
        })
      },
    },
  )

  const [
    createScheduledCancellation,
    { loading: isLoadingScheduled, error: errorScheduled },
  ] = useMutation<
    mutationScheduledCancellation,
    mutationScheduledCancellationVariables
  >(MUTATION_SCHEDULED_CANCELLATION, {
    onCompleted: () => {
      handleModalSubmit()
    },
    onError: (error, options) => {
      handleFetchErrorFunctional()(error, {
        location: window?.location || undefined,
        operation: {
          operationName: 'createScheduledCancellation',
          variables: options?.variables ?? undefined,
        },
        type: SentryErrorType.GQL_ERROR,
        userId: user.userId?.toString() ?? undefined,
      })
    },
  })

  const isLoading = isLoadingImmediate || isLoadingScheduled
  const error = errorImmediate || errorScheduled

  const formMethods = useForm<CancelDeviceModalFormSchema>({
    defaultValues: cancelDeviceFormDefaultValues(
      isScheduledCancellationEnabled,
      rental.isScheduledCancellation,
      rental.endAt,
    ),
    resolver: zodResolver(cancelDeviceModalFormSchema),
  })

  const {
    control,
    formState: { isDirty },
    watch,
    setValue,
  } = formMethods
  const cancellationType = watch(CANCEL_DEVICE_FORM_FIELDS.CANCELLATION_TYPE)
  const scheduledDate = watch(CANCEL_DEVICE_FORM_FIELDS.SCHEDULED_DATE)

  const hasError = !isEmpty(error)

  const handleCancellationTypeChange = useCallback(
    (type: CancellationTypeEnum) => {
      setValue(CANCEL_DEVICE_FORM_FIELDS.CANCELLATION_TYPE, type, {
        shouldDirty: true,
      })
    },
    [setValue],
  )

  const handleSubmit = formMethods.handleSubmit(data => {
    if (data.cancellationType === CancellationTypeEnum.SCHEDULED) {
      createScheduledCancellation({
        variables: {
          input: {
            cancellationDate: data.scheduledDate?.toISOString(),
            rentalID: rental.id,
          },
        },
      })
    } else {
      createCancelDeviceCaseItem({
        variables: {
          input: {
            message: data.message,
            organisationID: rental.organisation?.id ?? '0',
            rentalID: rental.id,
            requestingUserID: user.userId?.toString() ?? '0',
          },
        },
      })
    }
  })

  const canSubmit =
    isDirty &&
    (cancellationType === CancellationTypeEnum.IMMEDIATE ||
      (cancellationType === CancellationTypeEnum.SCHEDULED &&
        scheduledDate !== null))

  const submitButtonText = useMemo(() => {
    if (!isScheduledCancellationEnabled) {
      return intl.formatMessage({ id: 'Cancel_device' })
    }

    if (cancellationType === CancellationTypeEnum.SCHEDULED) {
      return intl.formatMessage({
        id: rental.isScheduledCancellation
          ? 'CancelDevice_updateCancellation_title'
          : 'CancelDevice_scheduleCancellation_title',
      })
    }

    return intl.formatMessage({ id: 'CancelDevice_cancelNow_title' })
  }, [
    cancellationType,
    intl,
    isScheduledCancellationEnabled,
    rental.isScheduledCancellation,
  ])

  return (
    <Card>
      <CardHeader
        color="red"
        e2eSelector={E2E_CANCEL_DEVICE_HEADER}
        icon={iconCancelDevice}
      >
        <FormattedMessage id="Cancel_device" />
      </CardHeader>

      <CardBody>
        {hasError && <AlertWithError error={error} />}

        <FormProvider {...formMethods}>
          <Form id={CANCEL_DEVICE_MODAL_FORM} onSubmit={handleSubmit}>
            {isScheduledCancellationEnabled && (
              <CancellationTypeSelector
                leadTime={scheduledCancellationConfig?.leadTime}
                onSelect={handleCancellationTypeChange}
                selectedType={cancellationType}
              />
            )}

            {cancellationType === CancellationTypeEnum.SCHEDULED &&
              scheduledCancellationConfig && (
                <div className="mb-3">
                  <Label
                    className="mb-2"
                    htmlFor={CANCEL_DEVICE_DATE_PICKER_ID}
                  >
                    <FormattedMessage id="CancelDevice_effectiveDate_label" />
                  </Label>
                  <Controller
                    control={control}
                    name={CANCEL_DEVICE_FORM_FIELDS.SCHEDULED_DATE}
                    render={({
                      field: { onChange, value },
                      fieldState: { error: fieldError },
                    }) => (
                      <SingleDatePicker
                        ariaDescribedBy={
                          scheduledCancellationConfig.leadTime
                            ? CANCEL_DEVICE_LEAD_TIME_INFO_ID
                            : undefined
                        }
                        ariaLabel={intl.formatMessage({
                          id: 'CancelDevice_effectiveDate_label',
                        })}
                        e2eSelector={E2E_CANCEL_DEVICE_DATE_PICKER}
                        hasError={!!fieldError}
                        id={CANCEL_DEVICE_DATE_PICKER_ID}
                        maxDate={maxCancellationDate}
                        minDate={scheduledCancellationConfig.showDatesFrom}
                        onChange={onChange}
                        onlyMonthEnd={
                          scheduledCancellationConfig.fullMonthRentalEnabled
                        }
                        placeholder={intl.formatMessage({
                          id: 'MultidatePicker_placeholder',
                        })}
                        value={value}
                      />
                    )}
                  />
                </div>
              )}

            {cancellationType === CancellationTypeEnum.IMMEDIATE && (
              <FormOptionalMessage />
            )}
          </Form>
        </FormProvider>

        <ButtonGroup
          leftButton={{
            onClick: onClose,
            text: intl.formatMessage({ id: 'Cancel' }),
          }}
          rightButton={
            hasError || !cancellationType
              ? undefined
              : {
                  'data-e2e': E2E_CANCEL_DEVICE_SUBMIT,
                  disabled: isLoading || !canSubmit,
                  form: CANCEL_DEVICE_MODAL_FORM,
                  isLoading,
                  text: submitButtonText,
                  type: 'submit',
                }
          }
        />
      </CardBody>
    </Card>
  )
})
